import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FlagCanada from "@/assets/Img/Flags/flag-canada.png";
import FlagUS from "@/assets/Img/Flags/flag-us.png";

export type CountryCode = "USA" | "CAN";

interface CountryDropdownProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  className?: string;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  value,
  onChange,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation("cabo");

  const COUNTRY_OPTIONS: { label: string; value: CountryCode; flag: string }[] =
    [
      { label: t("USA"), value: "USA", flag: FlagUS },
      { label: t("CAN"), value: "CAN", flag: FlagCanada },
    ];

  const selected = COUNTRY_OPTIONS.find((c) => c.value === value);

  return (
    <div className={classNames("relative w-fit", className)}>
      <button
        type="button"
        className="flex justify-between items-center gap-3 bg-white rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all min-w-[280px]"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="font-medium text-base text-gray-900 whitespace-nowrap">
          {t("sendingFrom")}:
        </span>
        <div className="flex items-center gap-2">
          {selected && (
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200">
              <img
                src={selected.flag}
                alt={selected.label}
                className="w-8 h-8 object-cover rounded-full"
              />
            </span>
          )}
          <svg
            className={classNames(
              "w-6 h-6 text-gray-600 transition-transform",
              open ? "rotate-180" : "rotate-0"
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>
      {open && (
        <div className="absolute z-10 mt-2 left-0 w-full bg-white border border-gray-200 rounded-xl shadow-lg py-1">
          {COUNTRY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={classNames(
                "flex items-center gap-2 w-full px-4 py-2 hover:bg-gray-100 transition-all",
                option.value === value && "bg-gray-50 font-semibold"
              )}
              onClick={() => {
                setOpen(false);
                if (option.value !== value) onChange(option.value);
              }}
              role="option"
              aria-selected={option.value === value}
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200">
                <img
                  src={option.flag}
                  alt={option.label}
                  className="w-6 h-6 object-cover rounded-full"
                />
              </span>
              <span className="text-base text-gray-900">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
